import { ExclamationCircleFilled } from '@ant-design/icons';
import { Modal } from 'antd';
import type { ReactNode } from 'react';

import {
  CONFIRM_CANCEL_TEXT,
  CONFIRM_OK_BUTTON_COLOR,
  CONFIRM_OK_TEXT
} from './constants';

export interface ConfirmModalOptions {
  title: ReactNode;
  content?: ReactNode;
  /** Defaults to the standard warning icon used by the Allocation Discard modal. */
  icon?: ReactNode;
  okText?: string;
  cancelText?: string;
  okButtonLoading?: boolean;
  /**
   * Renders the confirm button in antd's danger style instead of the standard
   * green, for a confirmation whose outcome destroys something the user cannot
   * get back (discarding pending edits, deleting a record).
   */
  isDestructive?: boolean;
  onOk?: () => void | Promise<void>;
  onCancel?: () => void;
}

/**
 * Opens the standardized confirmation modal shared across Panorama: a ghost
 * `Cancel` button paired with a green `Confirm` button, matching the Field
 * Allocation discard pattern — or a red one when `isDestructive` is set. Wraps
 * antd's imperative `Modal.confirm`.
 */
const showConfirmModal = ({
  title,
  content,
  icon = <ExclamationCircleFilled />,
  okText = CONFIRM_OK_TEXT,
  cancelText = CONFIRM_CANCEL_TEXT,
  okButtonLoading,
  isDestructive,
  onOk,
  onCancel
}: ConfirmModalOptions) =>
  Modal.confirm({
    title,
    content,
    icon,
    okText,
    cancelText,
    okButtonProps: {
      loading: okButtonLoading,
      danger: isDestructive,
      // The green is the affirmative-action color; a destructive confirm keeps
      // antd's danger palette instead of overriding it back to green.
      style: isDestructive
        ? undefined
        : {
            backgroundColor: CONFIRM_OK_BUTTON_COLOR,
            borderColor: CONFIRM_OK_BUTTON_COLOR
          }
    },
    onOk,
    onCancel
  });

export default showConfirmModal;
